import React, { useState } from 'react';
import { styled } from '@mui/material';
import { Text } from 'library';
import { ITeacher } from 'models';
import { useClient } from 'shared';
import { PermissionType, useUserContext } from 'UserContext';
import ShowcaseTeacherRow from './ShowcaseTeacherRow';
import InviteButton from './InviteButton';
import ViewMoreTeachers from './ViewMoreTeachers';
import { BasicCard } from './BasicCard';

interface ITeacherShowcase {
    workers: ITeacher[];
}

export function TeacherShowcase({ small }: { small?: boolean }) {
    const { data, error, loading } = useClient<ITeacherShowcase>({ url: 'api/dashboard/worker-spotlight/' });
    const [selectedWorkers, setSelectedWorkers] = useState<ITeacher[]>([]);
    const { hasPermission } = useUserContext();
    const canPostJobs = hasPermission(PermissionType.PostJob);

    function toggleSelected(worker: ITeacher) {
        if (selectedWorkers.some((sw) => sw.id === worker.id)) {
            setSelectedWorkers(selectedWorkers.filter((x) => x.id !== worker.id));
        } else {
            setSelectedWorkers([...selectedWorkers, worker]);
        }
    }

    if (error || data?.workers?.length === 0 || loading) return null;

    return (
        <BasicCard loading={loading} small={small || false}>
            <CardHeader>
                <Text variant="h1">Available Tandem Teachers</Text>
                {canPostJobs && (
                    <InviteButton selectedWorkers={selectedWorkers} setSelectedWorkers={setSelectedWorkers} />
                )}
            </CardHeader>
            <TeacherList>
                {data?.workers?.map((worker: ITeacher) => (
                    <ShowcaseTeacherRow
                        key={worker.id}
                        worker={worker}
                        selected={selectedWorkers.some((sw) => sw.id === worker.id)}
                        onChange={toggleSelected}
                        showcaseHighlight
                    />
                ))}
            </TeacherList>
            <ViewMoreContainer>
                <ViewMoreTeachers showcaseWorkers={data?.workers} />
            </ViewMoreContainer>
        </BasicCard>
    );
}

const CardHeader = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 16,
});

const TeacherList = styled('div')({
    display: 'flex',
    flexDirection: 'column',
});

const ViewMoreContainer = styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 8,
});
